import { styled } from "@mui/material/styles";
import LogoutIcon from "@mui/icons-material/Logout";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Panel } from "../../components/primitives/surfaces/Panel";
import { NavItem } from "../../components/primitives/navigation/NavItem";
import { useUserContext } from "../../context/UserContext";
import { ROUTES, signInPath } from "../../route_paths";
import { ACCOUNT_SECTIONS } from "./account_sections";

const SIDEBAR_WIDTH = 272;
/** The height of the sticky top bar at the widths where the section nav is sticky too (the default
 *  MUI toolbar from `sm` up). */
const TOP_BAR_HEIGHT = 64;

const Shell = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  maxWidth: 1360,
  minHeight: "100dvh",
  margin: "0 auto",
  padding: theme.spacing(4, 2, 0),
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: theme.spacing(3),
    padding: theme.spacing(6, 3, 0),
  },
}));

const SectionNav = styled("nav")(({ theme }) => ({
  flexShrink: 0,
  [theme.breakpoints.up("md")]: {
    position: "sticky",
    // Sticks below the top bar rather than behind it.
    top: `calc(${TOP_BAR_HEIGHT}px + ${theme.spacing(3)})`,
    // Clears the page header (compact title + subtitle + the page's gap) so the card lines up
    // with the first panel; headers on this space are one line each.
    marginTop: theme.spacing(14.5),
    width: SIDEBAR_WIDTH,
  },
}));

const NavItems = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(0.5),
  overflowX: "auto",
  [theme.breakpoints.up("md")]: { flexDirection: "column", overflowX: "visible" },
}));

const SignOutSlot = styled("div")(({ theme }) => ({
  marginLeft: "auto",
  [theme.breakpoints.up("md")]: {
    marginLeft: 0,
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const Content = styled("main")({
  flex: 1,
  minWidth: 0,
  width: "100%",
});

/**
 * The account space: a navigation of its sections (a card beside the content on wide screens, a
 * row above it on narrow ones), ending with Sign out, then the section shown. Visitors without an
 * account are sent to sign in and back.
 */
export default function AccountLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session, loading, signOut } = useUserContext();
  const { pathname, search } = useLocation();

  if (loading) return null;
  if (!session || session.user.is_anonymous) {
    return <Navigate to={signInPath(`${pathname}${search}`)} replace />;
  }

  async function handleSignOut() {
    await signOut();
    navigate(ROUTES.landing.path(), { replace: true });
  }

  return (
    <Shell>
      <SectionNav aria-label={t("account.title")}>
        <Panel pad="sm">
          <NavItems>
            {ACCOUNT_SECTIONS.map((section) => (
              <NavItem
                key={section.path}
                to={section.path}
                label={t(section.labelKey)}
                icon={section.icon}
              />
            ))}
            <SignOutSlot>
              <NavItem
                label={t("nav.signOut")}
                icon={<LogoutIcon />}
                onSelect={() => void handleSignOut()}
              />
            </SignOutSlot>
          </NavItems>
        </Panel>
      </SectionNav>
      <Content>
        <Outlet />
      </Content>
    </Shell>
  );
}
